import React, { useState, useEffect } from "react";
import { Grid, Button, Icon } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import FormField from "../../layouts/applications/wizard/components/FormField";
import { styled } from "@mui/system";
import PropTypes from "prop-types";

/**
 * @param {Object} props
 * @param {function} props.onDataChange - Callback function to handle data changes.
 */

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#000",
    color: "rgba(255, 255, 255, 1)",
    minWidth: "min-content",
    height: "min-content",
    textAlign: "left",
    fontSize: "0.65rem",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));
const OddFactorPriceSettings = ({ initialData, onDataChange, onValidationError }) => {
  const [operators, setOperators] = useState([
    { value: "x", label: "Multiplier: x" },
    { value: "+", label: "Increasement: +" },
  ]);

  const [infoData, setInfoData] = useState("1.00");
  const [lines, setLines] = useState(initialData);
  const [calculateManipulatedOddError, setCalculateManipulatedOddError] = useState([{}]);
  const [barrierStaticErrors, setBarrierStaticErrors] = useState([{}]);
  const [operatorType, setOperatorType] = useState("");

  const notifyParent = (newLines) => {
    if (onDataChange) {
      onDataChange(newLines);
    }
  };

  const handleInputValueChange = (e, index, field) => {
    const newLines = [...lines];
    const updatedErrors = [...barrierStaticErrors];

    newLines[index][field] = e.target.value;
    setLines(newLines);

    // Validation logic
    if (e.target.value === "") {
      updatedErrors[index] = { odd_factor: "The odd factor cannot be empty." };
    } else if (operatorType === "x" && parseFloat(e.target.value) === 0) {
      updatedErrors[index] = { odd_factor: "The odd factor cannot be zero." };
      e.target.value = parseFloat(e.target.value).toFixed(2);
      setOperatorType("");
    } else {
      updatedErrors[index] = [];
    }

    setBarrierStaticErrors(updatedErrors);
    checkForValidationErrors();
    notifyParent(newLines);
  };

  const handleFiltersSelectionsChange = (value, key, index) => {
    const newLines = [...lines];
    newLines[index][key] = value;

    //default value for the select option depends the value
    if (key === "operator") {
      newLines[index].value = value === "x" ? Number(1).toFixed(2) : Number(0).toFixed(2);
    }

    setLines(newLines);
    notifyParent(newLines);
  };

  const addNewLine = (e) => {
    e.preventDefault();
    const defaultOperator = operators[0];
    const defaultValue =
      defaultOperator.value === "x" ? Number(1).toFixed(2) : Number(0).toFixed(2);

    setLines([...lines, { value: defaultValue, operator: defaultOperator.value }]);
    setBarrierStaticErrors([...barrierStaticErrors, {}]);
    notifyParent([...lines, { value: defaultValue, operator: defaultOperator.value }]);
  };

  const removeLine = (e, indexToRemove) => {
    e.preventDefault();
    if (lines.length > 1) {
      const newLines = lines.filter((_, index) => index !== indexToRemove);
      const updatedErrors = barrierStaticErrors.filter((_, index) => index !== indexToRemove);

      setLines(newLines);
      setBarrierStaticErrors(updatedErrors);
      checkForValidationErrors();
      notifyParent(newLines);
    }
  };

  const calculateManipulatedOddExample = () => {
    let manipulatedOdd = 1.23; // Example starting value
    let manipulatedOddError = false;

    lines.forEach((line) => {
      const operator = line.operator;
      const value = parseFloat(line.value);
      if (!isNaN(value)) {
        switch (operator) {
          case "+":
            manipulatedOdd += value;
            break;
          case "x":
            manipulatedOdd *= value;
            if (value === 0) {
              manipulatedOddError = true;
              setOperatorType("x");
            }
            break;
          default:
            break;
        }
      }
    });

    const error =
      manipulatedOddError === true
        ? [
            {
              calculateManipulatedOddExample:
                "The manipulated odd value should not be equal to zero because of the multiplier.",
            },
          ]
        : [];
    setCalculateManipulatedOddError(error);
    checkForValidationErrors();
    return manipulatedOdd.toFixed(2);
  };

  const handleFocusOut = (e, index) => {
    e.preventDefault();
    const updatedErrors = [...barrierStaticErrors];

    if (e.target.value === "") {
      updatedErrors[index] = { odd_factor: "The odd factor cannot be empty." };
    } else if (operatorType === "x" && parseFloat(e.target.value) === 0) {
      updatedErrors[index] = { odd_factor: "The odd factor cannot be zero." };
      setOperatorType("");
      e.target.value = parseFloat(e.target.value).toFixed(2);
    } else {
      e.target.value = parseFloat(e.target.value).toFixed(2);
      updatedErrors[index] = {};
    }

    setBarrierStaticErrors(updatedErrors);
    checkForValidationErrors();
    handleInputValueChange(e, index, "value");
  };

  const handleInitOddFactor = () => {
    const updatedErrors = [...barrierStaticErrors];
    if (lines[0].value === "") {
      updatedErrors[0] = { odd_factor: "The odd factor cannot be empty." };
    } else if (operatorType === "x" && parseFloat(lines[0].value) === 0) {
      updatedErrors[0] = { odd_factor: "The odd factor cannot be zero." };
      setOperatorType("");
      lines[0].value = parseFloat(lines[0].value).toFixed(2);
    } else {
      lines[0].value = parseFloat(lines[0].value).toFixed(2);
      updatedErrors[0] = {};
    }

    setBarrierStaticErrors(updatedErrors);
    checkForValidationErrors();
  };

  const checkForValidationErrors = () => {
    const hasErrors =
      Boolean(calculateManipulatedOddError) || barrierStaticErrors.some((error) => error);

    const combinedError = [
      ...calculateManipulatedOddError,
      ...barrierStaticErrors.flatMap((error) =>
        Object.entries(error || {})
          .filter(([_, value]) => typeof value === "string")
          .map(([key, value]) => ({ [key]: value }))
      ),
    ];

    onValidationError(hasErrors ? combinedError : []);
  };

  useEffect(() => {
    const result = calculateManipulatedOddExample();
    setInfoData(result);
  }, [lines]);

  useEffect(() => {
    checkForValidationErrors();
  }, [calculateManipulatedOddError, barrierStaticErrors]);

  useEffect(() => {
    handleInitOddFactor();
  }, []);

  return (
    <React.Fragment>
      <Grid item xs={3} md={3}>
        <SoftBox
          p={1.2}
          mb={0}
          mt={0}
          ml={2}
          borderRadius="lg"
          bgColor="info"
          color="white"
          shadow="lg"
          style={{
            display: infoData ? "" : "none",
            height: "fit-content",
            width: "max-content",
            position: "relative",
            lineHeight: 1,
          }}
        >
          <Icon id="" style={{ marginRight: "10px" }}>
            info_icon
          </Icon>
          <span className="wizard-formfield-info">
            Odd value example: 1.23. The manipulated odd value: {infoData}
          </span>
          <SoftTypography style={{ fontSize: "11px", color: "red" }}>
            {calculateManipulatedOddError
              .flatMap((err) =>
                Object.entries(err).map(([_, message]) =>
                  typeof message === "string" ? message : ""
                )
              )
              .filter(Boolean)
              .join(", ")}
          </SoftTypography>
        </SoftBox>
      </Grid>

      {Array.isArray(lines) && lines.length > 0
        ? lines.map((line, index) => (
            <Grid container spacing={2} item xs={12} md={12} key={index} alignItems="center">
              <Grid item xs={3} md={3}>
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Select Odds mathematical operation
                  </SoftTypography>
                  <HtmlTooltip
                    placement="top"
                    title={
                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                        <div style={{ display: "block", fontSize: "14px" }}>
                          {
                            "This operator will be 'described' the mathematic calculation with the odd factor. Τhe priority appears in the order you set the math's operators. For example, if an odd's value is 10 and we have multiplier with value 0.2 and inceasement with value -0.5, then it will be (10* 0.2) - 0.5 = 1.5"
                          }
                        </div>
                      </div>
                    }
                  >
                    <Icon className="info-icon">info_outlined</Icon>
                  </HtmlTooltip>
                </SoftBox>
                <SoftSelect
                  id={`mathematical-operator-${index}`}
                  value={operators.find((op) => op.value === line.operator)}
                  options={operators}
                  onChange={(item) => handleFiltersSelectionsChange(item.value, "operator", index)}
                />
              </Grid>

              <Grid item xs={3} md={3} style={{ position: "relative" }}>
                <FormField
                  id={`barrier-static-${index}`}
                  type="number"
                  name={`barrier-static-${index}`}
                  error={!!Object.keys(barrierStaticErrors[index] || {}).length}
                  success={!Object.keys(barrierStaticErrors[index] || {}).length}
                  label="Odds factor"
                  info="This factor will be 'added' to all Odds at the end of the odds manipulation. Can be Positive or Negative"
                  infoPlacement="top"
                  inputProps={{
                    maxLength: 10,
                    step: "0.01",
                    inputMode: "decimal",
                  }}
                  infoWidth="200"
                  placeholder="Eg. 10.00"
                  step="0.01"
                  value={line.value}
                  onBlur={(e) => {
                    e.preventDefault();
                    handleFocusOut(e, index, "value");
                  }}
                  onChange={(e) => {
                    e.preventDefault();
                    let value = e.target.value;
                    if (/^-?\d*\.?\d{0,2}$/.test(value)) {
                      handleInputValueChange(e, index, "value");
                    } else {
                      e.target.value = "invalid";
                      handleInputValueChange(e, index, "value");
                    }
                  }}
                />
                {barrierStaticErrors[index] && (
                  <span
                    className="validation-error"
                    style={{ position: "absolute", top: "100%", marginTop: "-0.3rem" }}
                  >
                    {Object.values(barrierStaticErrors[index] || {})
                      .map((err) => (typeof err === "string" ? err : ""))
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                )}
              </Grid>

              <Grid item xs={1} md={1} display="flex" alignItems="center">
                <Tooltip title="Delete odd reaction" arrow>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                      color: "#8392ab",
                      minWidth: "10px",
                      minHeight: "10px",
                      padding: "8px",
                      "&:hover": {
                        backgroundColor: "#fff",
                      },
                    }}
                    onClick={(e) => removeLine(e, index)}
                    disabled={lines.length === 1}
                    style={{ margin: "0 0.5rem -1.8rem 0.2rem" }}
                  >
                    <Icon> delete </Icon>
                  </Button>
                </Tooltip>
                {index === lines.length - 1 && (
                  <Tooltip title="Add odd reaction" arrow>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{
                        color: "#8392ab",
                        backgroundColor: "#fff",
                        borderColor: "#8392ab",
                        border: "1px solid",
                        minWidth: "10px",
                        minHeight: "10px",
                        padding: "8px",
                        "&:hover": {
                          backgroundColor: "#fff",
                          borderColor: "#8392ab",
                        },
                      }}
                      onClick={addNewLine}
                      style={{ margin: "0 0 -1.8rem 0.2rem" }}
                    >
                      <Icon> add </Icon>
                    </Button>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          ))
        : null}
    </React.Fragment>
  );
};

OddFactorPriceSettings.propTypes = {
  initialData: PropTypes.array.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationError: PropTypes.func.isRequired,
};

export default OddFactorPriceSettings;
